import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Row from "antd/es/row";
import { ColumnType, TablePaginationConfig } from "antd/es/table";
import Tag from "antd/es/tag";
import { IntlShape, useIntl } from "react-intl";

import { EntityType, Group, Person } from "@mapmycustomers/shared/types/entity";
import { AvatarWithName } from "@mapmycustomers/ui";

import DaysOverCadence from "@app/component/cadence/DaysOverCadence";
import { PhoneNumber } from "@app/component/field";
import Table from "@app/component/Table";
import LongContentParagraph from "@app/component/typography/LongContentParagraph";
import MapFilter from "@app/scene/reports/enums/MapFilter";
import { DEFAULT_ENTITY_LIST_PAGE_SIZE } from "@app/scene/reports/utils/const";
import { showEntityView } from "@app/store/entityView/actions";

import styles from "./BasePinCard.module.scss";

const useColumns = (intl: IntlShape, filter: MapFilter): ColumnType<Person>[] =>
  useMemo(
    () => [
      {
        dataIndex: "person",
        key: "person",
        render: (ignore: any, person: Person) => (
          <Row>
            <Col>
              <div className={styles.personIcon}>
                <FontAwesomeIcon icon={faUser} />
              </div>
            </Col>
            <Col>
              {person.name}
              <br />
              <PhoneNumber className={styles.phoneNumber} value={person.phone} />
            </Col>
          </Row>
        ),
        title: intl.formatMessage({
          id: "reports.activity.peopleList.column.person",
          defaultMessage: "Person",
          description: "Title of a Company column in Person table on reports page",
        }),
        width: "25%",
      },
      {
        dataIndex: "address",
        key: "address",
        title: intl.formatMessage({
          id: "reports.activity.peopleList.column.address",
          defaultMessage: "Address",
          description: "Title of a Address column in Company table on reports page",
        }),
        width: "25%",
      },
      {
        dataIndex: "owner",
        key: "owner",
        render: (ignore: any, person: Person) => <AvatarWithName user={person.user} />,
        title: intl.formatMessage({
          id: "reports.activity.peopleList.column.owner",
          defaultMessage: "Owner",
          description: "Title of a Owner column in Owner table on reports page",
        }),
        width: "20%",
      },
      {
        dataIndex: "lastContact",
        key: "lastContact",
        render: (ignore: any, person: Person) =>
          filter === MapFilter.PEOPLE_30_DAYS_NO_CONTACT ? (
            <Tag className={styles.tag} color="error">
              {person.noContactDaysOut
                ? intl.formatMessage(
                    {
                      id: "reports.activity.peopleNoContactList.column.lastContact.days",
                      defaultMessage: "{count} days ago",
                      description: "Last contact column value in People table on reports page",
                    },
                    { count: person.noContactDaysOut }
                  )
                : intl.formatMessage({
                    id: "reports.activity.peopleNoContactList.column.lastContact.never",
                    defaultMessage: "Not yet contacted",
                    description: "Last contact column value in People table on reports page",
                  })}
            </Tag>
          ) : person.groups && person.groups.length > 0 ? (
            <LongContentParagraph noMargin rows={3}>
              {person.groups.map((group: Group) => (
                <DaysOverCadence group={group} intl={intl} key={group.id} />
              ))}
            </LongContentParagraph>
          ) : (
            <Tag className={styles.tag} color="error">
              {intl.formatMessage({
                id: "reports.activity.peopleList.column.lastContact.never",
                defaultMessage: "Not yet contacted",
                description: "Last contact column value in People table on reports page",
              })}
            </Tag>
          ),
        title:
          filter === MapFilter.PEOPLE_OVER_CADENCE
            ? intl.formatMessage({
                id: "reports.activity.peopleOverCadenceList.column.daysOverCadence",
                defaultMessage: "Days past due",
                description: "Title of a Days over Cadence column in People table on reports page",
              })
            : intl.formatMessage({
                id: "reports.activity.peopleNoContactList.column.lastContact",
                defaultMessage: "Last contact",
                description: "Title of a Last Contact column in People table on reports page",
              }),
        width: "20%",
      },
    ],
    [filter, intl]
  );

interface Props {
  filter: MapFilter;
  onLoadData?: (page: number) => void;
  onShowEntityView: typeof showEntityView;
  page: number;
  people: Person[];
  total: number;
}

const PeopleList: React.FC<Props> = ({
  filter,
  onLoadData,
  onShowEntityView,
  page,
  people,
  total,
}) => {
  const intl = useIntl();
  const columns = useColumns(intl, filter);

  const rowEventHandlers = useCallback(
    (person: Person) => ({
      onClick: () => onShowEntityView({ entityId: person.id, entityType: EntityType.PERSON }),
    }),
    [onShowEntityView]
  );

  const paginationOptions: TablePaginationConfig = useMemo(
    () => ({
      current: page + 1,
      defaultPageSize: DEFAULT_ENTITY_LIST_PAGE_SIZE,
      onChange: (page) => onLoadData?.(page - 1),
      position: ["bottomCenter"],
      showSizeChanger: false,
      total,
    }),
    [page, onLoadData, total]
  );

  return (
    <div className={styles.tableContainer}>
      <Table<Person>
        clickableRows
        columns={columns}
        dataSource={people}
        onRow={rowEventHandlers}
        pagination={paginationOptions}
        rowKey="id"
        showBorder
      />
    </div>
  );
};

const mapStateToProps = {
  onShowEntityView: showEntityView,
};

export default connect(null, mapStateToProps)(PeopleList);
